import styled from "@emotion/styled";
import { Stack, Tooltip } from "@mui/material";
import { ExchangeIcon } from "src/Common/components/ExchangeIcon";
import { useMarkAsReadCrypto } from "src/Modules/Arbitrage/context";
import {
  ArbitrageOrderBooks,
  ArbitrageOrderBooksItem,
  InfoValuesType,
} from "src/Modules/Arbitrage/types";

type Props = ArbitrageOrderBooksItem & {
  valueType: InfoValuesType;
  cryptoSymbol: ArbitrageOrderBooks["symbol"];
  cryptoName: ArbitrageOrderBooks["name"];
};

function formatAge(ts: number): string {
  if (!ts) return "";
  const seconds = Math.round((Date.now() - ts) / 1000);
  if (seconds < 60) return `${seconds}s`;
  return `${Math.round(seconds / 60)}m`;
}

export const ExchangePrice = ({
  cryptoSymbol,
  cryptoName,
  quoteCoin,
  exchangeId,
  exchangeName,
  topAsk,
  topBid,
  formattedBuyEffectivePrice,
  formattedSellEffectivePrice,
  formattedCoinsToBuy,
  formattedCoinsToSell,
  lastOrderbookUpdate,
  valueType,
  address,
}: Props) => {
  const { open } = useMarkAsReadCrypto();

  const isMinValue = valueType === InfoValuesType.MIN;

  return (
    <Container
      onDoubleClick={() =>
        open({
          cryptoSymbol: cryptoSymbol,
          name: cryptoName,
          quoteCoin: quoteCoin,
          exchange: exchangeId,
          address,
        })
      }
    >
      <Exchange>
        <ExchangeIcon
          name={exchangeId}
          style={{ height: "2rem", padding: 0 }}
        />
        <ExchangeName>
          {exchangeName
            ?.toUpperCase()
            .split(/[\s-]+/)
            .map((e) => (
              <div key={e}>{e}</div>
            ))}{" "}
          <span>{quoteCoin}</span>
        </ExchangeName>
      </Exchange>

      <PriceContainer>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.4}
          divider={<span>-</span>}
        >
          <Ask>{Number(topBid)}</Ask>
          <Bid>{Number(topAsk)} </Bid>
        </Stack>
        <Tooltip title={`${isMinValue ? "buy" : "sell"} eff price`}>
          <EffectivePrice>
            {isMinValue
              ? Number(formattedBuyEffectivePrice)
              : Number(formattedSellEffectivePrice)}
          </EffectivePrice>
        </Tooltip>

        <Tooltip title={`coins to ${isMinValue ? "buy" : "sell"}`}>
          <CoinsToBuy>
            {isMinValue ? formattedCoinsToBuy : formattedCoinsToSell ?? "-"}
          </CoinsToBuy>
        </Tooltip>
        {lastOrderbookUpdate > 0 && (
          <Tooltip title={new Date(lastOrderbookUpdate).toLocaleTimeString()}>
            <ObAge>{formatAge(lastOrderbookUpdate)}</ObAge>
          </Tooltip>
        )}
      </PriceContainer>
    </Container>
  );
};

const Container = styled.div(() => ({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  cursor: "pointer",
  "&:hover": {
    opacity: 0.8,
  },
  "&:active": {
    opacity: 1,
  },
}));

const Exchange = styled.span({
  paddingTop: "0.2rem",
  display: "flex",
  alignItems: "center",
});

const ExchangeName = styled(Stack)({
  fontWeight: 700,
  fontSize: "0.7rem",
  "& span": {
    fontWeight: 300,
  },
});

const PriceContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  alignSelf: "center",
});

const Ask = styled.div(({ theme }) => ({
  wordBreak: "keep-all",
  fontWeight: 300,
  fontSize: "0.7rem",
  color: theme.palette.success.main,
}));

const Bid = styled.div(({ theme }) => ({
  wordBreak: "keep-all",
  fontWeight: 300,
  fontSize: "0.7rem",
  color: theme.palette.error.main,
}));

const EffectivePrice = styled.span({
  fontWeight: 700,
  fontSize: "0.7rem",
  "& span": {
    fontWeight: 300,
  },
});

const CoinsToBuy = styled.span(() => ({
  fontWeight: 300,
  fontSize: "0.7rem",
}));

const ObAge = styled.span(({ theme }) => ({
  fontWeight: 300,
  fontSize: "0.6rem",
  color: theme.palette.text.secondary,
}));
